import React, { useEffect } from 'react';
import Input from '@/components/General/inputs/Input';
import IconPickerInput from '@/components/General/inputs/IconPickerInput';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import InputSection from '@/components/General/inputs/InputSection';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { saveSmallGroupType } from '@/data/smallGroups';
import Toast from 'react-native-toast-message';
import Callout from '@/components/General/Callout';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import ModalWrapper from '@/components/General/ModalWrapper';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import SelectInput from '@/components/General/inputs/SelectInput';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

const SMALL_GROUP_ICONS = [
    "person-outline",
    "people-outline",
    "book-outline",
    "heart-outline",
    "star-outline",
    "home-outline",
    "flag-outline",
    "school-outline",
    "musical-notes-outline",
    "globe-outline",
    "flame-outline",
    "leaf-outline",
    "infinite-outline",
    "chatbubbles-outline",
    "hand-left-outline",
    "trophy-outline",
    "calendar-outline",
    "compass-outline",
    "construct-outline",
    "megaphone-outline",
    "briefcase-outline",
];

const EMPTY_DRAFT = {};

export default function AddSmallGroupTypeModal() {
    const { id, fromScreen, fromStoreKey } = useLocalSearchParams()
    const storeKey = id || "new"

    const queryClient = useQueryClient()
    const router = useRouter()

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const smallGroupTypeDetails = useEditScreenStore(s => s.AddSmallGroupTypeModal?.[storeKey]) || EMPTY_DRAFT

    const { data: smallGroupTypeToEdit } = Query.smallGroupTypes.byId({id, options: { enabled: !!id }});
    const sgCustomFieldsQuery = Query.customFields.all({module: "SmallGroupType"});
    
    const saveSmallGroupTypeMutation = useMutation({
        mutationFn: saveSmallGroupType,
        onSuccess: (data) => {
            queryClient.invalidateQueries({queryKey: QueryKeys.smallGroupTypes.all()});
            queryClient.invalidateQueries({queryKey: ['smallGroups']});
            queryClient.invalidateQueries({queryKey: ['people']});
            Toast.delayShow({type: 'success', text2: 'New Small Group Type has been created successfully!'})
            removeEditScreenData("AddSmallGroupTypeModal", storeKey)

            if(fromScreen){
                useEditScreenStore.getState().dataChangeFunction?.(data)
                router.dismissTo({pathname:fromScreen, params: fromStoreKey && fromStoreKey != "new" ? {id: fromStoreKey} : undefined});
            }
            else{
                redirectToDefault(router, "AddSmallGroupTypeModal")
            }
        }
    })

    /*** Effects ***/
    useEffect(() => {
        if(smallGroupTypeToEdit){
            const displayMap = {};
            (smallGroupTypeToEdit.CustomFields || []).forEach(f => { displayMap[f.id] = f.displayOn || 'group'; });
            setSmallGroupTypeDetails({
                ...smallGroupTypeToEdit,
                customFieldDisplay: displayMap,
                originalValues: smallGroupTypeToEdit,
            })
        }
    }, [smallGroupTypeToEdit])

    // Default new types to auto-selecting members when reporting sessions.
    useEffect(() => {
        if(!id && smallGroupTypeDetails.autoSelectMembersOnSessionReport === undefined){
            setSmallGroupTypeDetails({ autoSelectMembersOnSessionReport: true })
        }
    }, [id])

    const setSmallGroupTypeDetails = (details) => {
        setEditScreenData("AddSmallGroupTypeModal", storeKey, details)
    }

    return (
        <ModalWrapper
            loading={saveSmallGroupTypeMutation.isPending}
            onConfirm={() => {
                const { originalValues, ...cleanDetails } = smallGroupTypeDetails;
                saveSmallGroupTypeMutation.mutate({...cleanDetails, originalValues})
            }} title={id ? "Update Small Group Type" : "New Small Group Type"}
        >
            <KeyboardAwareScrollView bottomOffset={100} style={{flex:1, width: "100%"}} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
                <Callout type={"info"} message="This will create a new type for Small Groups. It's recommended to give a singular name for the type (e.g. 'Small Group'). After creation you may create new Small Groups with individual leaders and members under this type." />

                <InputSection>
                    <Input
                        title="Name"
                        maxLength={120}
                        value={smallGroupTypeDetails.name}
                        onUpdate={(new_value) => {
                            setSmallGroupTypeDetails({name: new_value})
                        }}
                    />

                    <Input
                        title="Description"
                        multiline
                        value={smallGroupTypeDetails.description}
                        onUpdate={(new_value) => {
                            setSmallGroupTypeDetails({description: new_value})
                        }}
                    />

                    <SheetSelector
                        title="Leader Role"
                        type="roles"
                        value={smallGroupTypeDetails.Role}
                        onChange={(role) => {
                            setSmallGroupTypeDetails({
                                roleID: role?.id || null,
                                Role: role || null,
                            });
                        }}
                        helpText="Only people assigned to this role can be selected as a leader."
                    />

                    <IconPickerInput
                        title="Icon"
                        value={smallGroupTypeDetails.icon}
                        options={SMALL_GROUP_ICONS}
                        onUpdate={(val) => setSmallGroupTypeDetails({ icon: val })}
                    />

                    <SheetSelector
                        title="Custom Fields"
                        type="personCustomFields"
                        queryResult={sgCustomFieldsQuery}
                        multiple={true}
                        value={smallGroupTypeDetails.CustomFields || []}
                        onChange={(fields) => {
                            const displayMap = smallGroupTypeDetails.customFieldDisplay || {};
                            const newDisplay = {};
                            fields.forEach(f => { newDisplay[f.id] = displayMap[f.id] || 'group'; });
                            setSmallGroupTypeDetails({ CustomFields: fields, customFieldDisplay: newDisplay });
                        }}
                        helpText="Select which custom fields will be shown on small groups or sessions of this type."
                        testID="small-group-type-custom-fields"
                    />

                    {smallGroupTypeDetails.CustomFields?.length > 0 && (
                        smallGroupTypeDetails.CustomFields.map(field => (
                            <SelectInput
                                key={field.id}
                                title={field.name}
                                value={smallGroupTypeDetails.customFieldDisplay?.[field.id] || 'group'}
                                options={[
                                    { label: 'Small Group Creation', value: 'group' },
                                    { label: 'Session Creation', value: 'session' },
                                ]}
                                onUpdate={(val) => {
                                    const oldDisplay = smallGroupTypeDetails.customFieldDisplay || {};
                                    setSmallGroupTypeDetails({ customFieldDisplay: { ...oldDisplay, [field.id]: val } });
                                }}
                            />
                        ))
                    )}

                    <ToggleRow
                        label="Auto Select Members When Reporting Sessions"
                        description="When on, all members of the small group are pre-selected when reporting a session. Turn off for large groups where attendance varies."
                        value={smallGroupTypeDetails.autoSelectMembersOnSessionReport !== false}
                        onValueChange={(val) => setSmallGroupTypeDetails({ autoSelectMembersOnSessionReport: val })}
                    />

                </InputSection>
            </KeyboardAwareScrollView>
        </ModalWrapper> 
    );
}